import React from "react"

type SparklineProps = {
    data: number[]
    color: string
    width?: number
    height?: number
}

/**
 * Mini trend line — geometry ported verbatim from the prototype's widgets.js.
 *
 * Decorative only: it is marked aria-hidden because the production API exposes
 * no historical series, so the shape is derived from the current value (see
 * `sparkFromValue` in src/utils/ModernFormat.ts) and must never be read as a
 * real trend by assistive tech.
 */
export default function Sparkline(props: SparklineProps) {
    const {data, color, width = 96, height = 26} = props;

    const {pts, cx, cy} = React.useMemo(() => {
        const p = 2;
        const mn = Math.min.apply(null, data);
        const mx = Math.max.apply(null, data);
        const x = (i: number) => p + (i * (width - 2 * p)) / (data.length - 1 || 1);
        const y = (v: number) => height - p - ((v - mn) * (height - 2 * p)) / (mx - mn || 1);

        return {
            pts: data.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" "),
            cx: x(data.length - 1),
            cy: y(data[data.length - 1]),
        }
    }, [data, width, height]);

    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" focusable="false">
          <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round"
                    strokeLinecap="round"/>
          <circle cx={cx} cy={cy} r="2.4" fill={color}/>
      </svg>
    )
}
